import { useEffect, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { EducationHostedFormModel, EducationHostedFormSteps } from '../EducationHostedForm.types'
import { useNavigateForm } from '../hooks/useNavigateForm'
import { shouldRunEnrolmentCheck } from '../enrolmentCheck'

import { Step } from '@percent/education-hosted-form/ui/Step/Step'
import { Alert, ButtonText, Flex, FormField, TextInput } from '@percent/lemonade'
import { useEducationHostedForm } from '@percent/education-hosted-form/hooks/useEducationHostedForm/useEducationHostedForm'
import { useTrackEvent } from '@percent/education-hosted-form/analytics/useTrackEvent'
import { useOtp } from '@percent/education-hosted-form/hooks/useOtp/useOtp'
import { PrecheckType } from '@percent/education-hosted-form/api/hostedFormPrecheck/hostedFormPrecheck.types'

const OTP_LENGTH = 6
const DIGITS_ONLY = /^\d*$/

export function Otp() {
  const { t } = useTranslation()
  const { progress, otpCooldownEndsAt, mustReverifyOtp, setMustReverifyOtp, partnerConfig } = useEducationHostedForm()
  const { handleNextStep, handlePreviousStep } = useNavigateForm()
  const { getValues, setValue } = useFormContext<EducationHostedFormModel>()
  const trackEvent = useTrackEvent()
  const enrolmentCheckEnabled = shouldRunEnrolmentCheck(partnerConfig?.configuration)

  const [code, setCode] = useState('')
  const hasAutoSent = useRef(false)

  const {
    sendOtp,
    verifyOtp,
    clearVerifyError,
    clearSendError,
    cooldownSeconds,
    canResend,
    isVerifying,
    verifyErrorKey,
    sendErrorKey
  } = useOtp({
    onVerifySuccess: otpJwt => {
      setValue('otpJwt', otpJwt)
      // Instant-verify (precheck and/or EAV) needs a selected org + the config enabling it.
      const config = partnerConfig?.configuration
      const canInstantVerify =
        !!getValues('organisationId') &&
        (enrolmentCheckEnabled || !!config?.prechecks?.includes(PrecheckType.DomainMatch))

      handleNextStep(
        canInstantVerify ? EducationHostedFormSteps.PROCESSING_PAGE : EducationHostedFormSteps.DOCUMENT_UPLOAD
      )
    },
    onMaxAttemptsExceeded: () => handleNextStep(EducationHostedFormSteps.OTP_MAX_ATTEMPTS_EXCEEDED)
  })

  // Auto-send once per mount unless an unexpired cooldown is active.
  useEffect(() => {
    if (hasAutoSent.current) return

    hasAutoSent.current = true

    const cooldownActive = otpCooldownEndsAt !== null && otpCooldownEndsAt > Date.now()

    if (!cooldownActive) sendOtp()
  }, [otpCooldownEndsAt, sendOtp])

  // Banner only makes sense in the immediate JWT-expired bounce flow. Clear on
  // any exit (verify success, back-nav, refresh) so it can't go stale on re-entry.
  useEffect(() => () => setMustReverifyOtp(false), [setMustReverifyOtp])

  const handleCodeChange = (next: string) => {
    if (!DIGITS_ONLY.test(next)) return

    setCode(next)

    if (verifyErrorKey) clearVerifyError()
  }

  const handleVerify = () => verifyOtp(code)
  const handleResend = () => {
    if (!canResend) return

    trackEvent('education_form_button_clicked', { button: 'resend_otp', step: EducationHostedFormSteps.OTP })
    setCode('')
    clearVerifyError()
    clearSendError()
    sendOtp()
  }

  const activeErrorKey = sendErrorKey ?? verifyErrorKey

  const isCodeComplete = code.length === OTP_LENGTH
  // Exclude isSending: under React Strict Mode the dev double-mount triggers two
  // /otp/send mutations that race, leaving isPending stuck true (TanStack/query #5341).
  const isLoading = isVerifying

  return (
    <Step
      step={EducationHostedFormSteps.OTP}
      title={t('otp.title')}
      description={t('otp.description')}
      onBack={handlePreviousStep}
      onNext={handleVerify}
      nextButtonDisabled={!isCodeComplete}
      nextButtonLabel={t('otp.verifyButton')}
      progress={progress}
      isLoading={isLoading}
    >
      <Flex direction="column" gap={16} w="100%">
        {mustReverifyOtp && <Alert variant="warning">{t('otp.notice.mustReverify')}</Alert>}
        <FormField
          label={t('otp.codeLabel')}
          status={activeErrorKey ? 'danger' : 'default'}
          statusMessage={activeErrorKey ? t(activeErrorKey) : undefined}
          necessity="required"
          hideCharacterCount
        >
          <TextInput
            name="otp-code"
            value={code}
            onChange={e => handleCodeChange(e.target.value)}
            placeholder={t('otp.codePlaceholder')}
            inputMode="numeric"
            maxLength={OTP_LENGTH}
            autoComplete="one-time-code"
          />
        </FormField>
        <ButtonText
          variant="primary"
          size="sm"
          onClick={handleResend}
          // Drops isSending for the same Strict Mode reason as `isLoading` above.
          disabled={!canResend}
          data-testid="otp-resend-button"
        >
          {cooldownSeconds > 0 ? t('otp.resendCountdown', { seconds: cooldownSeconds }) : t('otp.resendButton')}
        </ButtonText>
      </Flex>
    </Step>
  )
}
